/**
 * Product Parsing and SEO Generation System
 * Based on DANK PRODUCT DATA STANDARDIZATION AND PARSING RULESET
 * 
 * SUMMARY (Section 8):
 * You're creating the unified, long-term blueprint for:
 * - Parsing all products safely
 * - Making URLs perfectly consistent
 * - Generating SEO titles/descriptions automatically
 * - Normalizing messy legacy data
 * - Supporting clean structure for future data
 * - Providing manual overrides when data is garbage
 * 
 * This creates a self-healing, structured, predictable product catalog system that survives:
 * - Bad vendor data
 * - Bad staff input
 * - Chaotic Cova naming
 * - Future scaling
 * - SEO consistency
 * 
 * This module implements comprehensive parsing for:
 * - Cannabis products (Flower, Pre-Rolls, Vapes, etc.)
 * - Accessories (with legacy data handling)
 * 
 * And generates:
 * - SEO-optimized URLs/slugs
 * - SEO titles
 * - SEO descriptions
 * 
 * OVERRIDE SYSTEM (Section 7):
 * This is the "break glass in case of emergency" feature.
 * Override fields you might include:
 * - brand
 * - product name
 * - classification
 * - variants (variant1, variant2)
 * - SEO title
 * - SEO description
 * 
 * If override is set → override wins.
 * If not → use parsed values.
 * 
 * This ensures:
 * - Staff can fix bad Cova data manually
 * - SEO can be customized
 * - System never breaks even when data is wild
 */

export interface ParsedProduct {
  brand: string | null;
  productName: string;
  classification: string | null;
  weight: string | null;
  variant1: string | null;
  variant2: string | null;
  isCannabis: boolean;
  isFlower: boolean;
}

/**
 * Accessory classifications that should use accessory parsing rules
 */
const ACCESSORY_CLASSIFICATIONS = [
  'accessories', 'ashtray', 'batteries and devices', 'glassware', 'grinder',
  'lighters', 'rolling papers', 'siliconeware', 'storage and care', 'merch',
  'trays', 'torches', 'cleaning', 'scales', 'keychains', 'replacement parts',
  'bowls', 'tools and dabbers', 'clothing'
];

/**
 * Cannabis classifications
 */
const CANNABIS_CLASSIFICATIONS = [
  'flower', 'pre-roll', 'preroll', 'pre roll', 'vape', 'cartridge', 'concentrate',
  'edible', 'oil', 'beverage', 'capsule', 'topical', 'seed'
];

/**
 * Determine if a product is cannabis (not accessory)
 * Based on classificationName
 */
export function isCannabisProduct(classificationName: string | null | undefined): boolean {
  if (!classificationName) return true; // Default to cannabis if unknown
  const lower = classificationName.toLowerCase();
  
  // Check if it's an accessory classification
  const isAccessory = ACCESSORY_CLASSIFICATIONS.some(acc => lower.includes(acc));
  if (isAccessory) return false;
  
  // Check if it's explicitly a cannabis classification
  const isCannabis = CANNABIS_CLASSIFICATIONS.some(can => lower.includes(can));
  if (isCannabis) return true;
  
  // Default to cannabis if unclear
  return true;
}

/**
 * Determine if a cannabis product is Flower
 */
export function isFlowerProduct(classificationName: string | null | undefined): boolean {
  if (!classificationName) return false;
  const lower = classificationName.toLowerCase();
  return lower.includes('flower');
}

/**
 * Extract weight/size from product name or sizeOrWeight field
 * 
 * IMPORTANT: Trust Cova sizeOrWeight field FIRST, name text is secondary
 * 
 * Logic:
 * 1. Trust Cova sizeOrWeight field FIRST
 * 2. Fallback: Extract from last pipe segment if it matches weight patterns
 * 3. Fallback: Pattern matching at end of name
 * 
 * Matches patterns like: "10x0.35g", "3x0.75g", "1g", "0.5g", "28g", etc.
 */
function extractWeight(name: string, sizeOrWeight?: string | null): string | null {
  // Trust Cova sizeOrWeight field FIRST
  if (sizeOrWeight) return sizeOrWeight;
  
  // Extract from last pipe segment if name has pipes
  const segments = name.split('|').map(s => s.trim()).filter(s => s.length > 0);
  if (segments.length > 1) {
    const lastSegment = segments[segments.length - 1];
    // Check if last segment matches weight patterns
    const weightPatterns = [
      /^\d+x?\d*\.?\d*\s*g$/i,  // "10x0.35g", "1g", "28g"
      /^\d+\s*pack$/i,           // "10 Pack"
      /^\d+\s*ml$/i,              // "100ml"
      /^\d+\s*mg$/i,              // "100mg"
    ];
    
    for (const pattern of weightPatterns) {
      if (pattern.test(lastSegment)) {
        return lastSegment;
      }
    }
  }
  
  // Fallback: Match weight patterns at the end of the name
  const weightPatterns = [
    /\d+x?\d*\.?\d*\s*g\s*$/i,  // "10x0.35g", "1g", "28g"
    /\d+\s*pack\s*$/i,           // "10 Pack"
    /\d+\s*ml\s*$/i,              // "100ml"
    /\d+\s*mg\s*$/i,              // "100mg"
  ];
  
  for (const pattern of weightPatterns) {
    const match = name.match(pattern);
    if (match) {
      return match[0].trim();
    }
  }
  
  return null;
}

/**
 * Parse cannabis product name
 * 
 * IMPORTANT: Trust Cova fields FIRST, name text is secondary
 * 
 * Expected format: Brand | Product Name | Classification | Weight
 * OR for Flower: Brand | Strain Name | Weight (no Classification in name)
 * 
 * Parsing logic:
 * - Brand: Trust Cova brandName field FIRST, fallback to first segment in name
 * - Product Name: Everything between Brand and Weight in pipe structure
 * - Classification: ALWAYS trust Cova classificationName field (never infer from name)
 * - Weight: Trust Cova sizeOrWeight field FIRST, fallback to last segment in name
 */
function parseCannabisProduct(
  name: string,
  classificationName: string | null,
  brandName: string | null,
  sizeOrWeight: string | null
): ParsedProduct {
  // Trust Cova classification field FIRST - determines if flower
  const isFlower = isFlowerProduct(classificationName);
  const segments = name.split('|').map(s => s.trim()).filter(s => s.length > 0);
  
  // Brand: Trust Cova brandName field FIRST, fallback to text (first segment)
  let brand: string | null = brandName || null;
  let productName = name;
  
  // Weight: Trust Cova sizeOrWeight field FIRST, fallback to last segment or pattern matching
  let weight: string | null = extractWeight(name, sizeOrWeight);
  
  // Fallback: Extract brand from first segment if Cova brand not provided
  if (!brand && segments.length > 0) {
    brand = segments[0];
  }
  
  // For cannabis products, extract product name from segments
  if (segments.length >= 2) {
    // Brand is first segment
    if (!brand) brand = segments[0];
    
    // Product name is everything between brand and weight
    // For non-flower: Brand | Product Name | Classification | Weight
    // For flower: Brand | Strain Name | Weight
    if (isFlower) {
      // Flower: Brand | Strain Name | Weight
      // According to ruleset: "Brand | Strain Name | Weight" with no Classification segment
      // Product name is everything between Brand and Weight (segment 2)
      if (segments.length >= 2) {
        // If we have weight extracted, it's likely the last segment
        // So product name is segment 2 (or segments 2 to second-to-last if weight not yet extracted)
        if (weight && segments[segments.length - 1] === weight) {
          // Weight is last segment, product name is everything in between
          productName = segments.slice(1, -1).join(' ').trim();
        } else {
          // Weight not found in last segment, check if last segment looks like weight
          const lastSegment = segments[segments.length - 1];
          if (/\d/.test(lastSegment) && (lastSegment.includes('g') || lastSegment.includes('mg') || lastSegment.includes('ml') || lastSegment.includes('pack'))) {
            // Last segment is weight
            weight = lastSegment;
            productName = segments.slice(1, -1).join(' ').trim();
          } else {
            // Last segment is not weight, so it's part of product name
            productName = segments.slice(1).join(' ').trim();
          }
        }
      } else {
        productName = segments[0] || name;
      }
    } else {
      // Non-flower: Brand | Product Name | Classification | Weight
      // Product name: Everything between Brand and Weight
      // Since we trust Cova classificationName field, we exclude classification from product name
      // So product name is segment 2 (between brand and weight/classification)
      if (segments.length >= 2) {
        // Segment 1 = Brand (already extracted from Cova or first segment)
        // Segment 2 = Product Name (everything between brand and weight)
        // Segment 3 = Classification (in name, but we trust Cova classificationName field)
        // Segment 4 = Weight (already extracted from Cova or last segment)
        productName = segments[1];
      } else {
        productName = segments[0] || name;
      }
    }
  } else if (segments.length === 1) {
    // Single segment - might be just product name
    productName = segments[0];
  }
  
  // Remove weight from product name if it's still there
  if (weight && productName.includes(weight)) {
    productName = productName.replace(new RegExp(weight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), '').trim();
  }
  
  // Remove brand from product name if it's still there
  if (brand && productName.toLowerCase().includes(brand.toLowerCase())) {
    const brandRegex = new RegExp(`\\b${brand.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
    productName = productName.replace(brandRegex, '').trim();
  }
  
  // Clean up product name
  productName = productName.replace(/\|/g, '').replace(/\s+/g, ' ').trim();
  
  // Ensure product name is not empty
  if (!productName) {
    // Fallback: use original name without brand and weight
    productName = name;
    if (brand) {
      productName = productName.replace(new RegExp(`^${brand.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*[|\\-–—]?\\s*`, 'i'), '').trim();
    }
    if (weight) {
      productName = productName.replace(new RegExp(weight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), '').trim();
    }
    productName = productName.replace(/\|/g, '').replace(/\s+/g, ' ').trim() || name;
  }
  
  return {
    brand,
    productName,
    classification: classificationName, // ALWAYS trust Cova classificationName field
    weight,
    variant1: null,
    variant2: null,
    isCannabis: true,
    isFlower,
  };
}

/**
 * Parse accessory product name
 * 
 * IDEAL FUTURE NAMING PATTERN (Standard):
 * Brand | Product Name | Variant 1 | Variant 2
 * 
 * Examples:
 * - Yocan | Uni Pro | 510 Battery | Red
 * - Dank Logo | Aluminum Grinder | 63mm | Black
 * - Pulsar | Wizard DL | 510 Pipe | Mahogany
 * 
 * This structure supports:
 * - Color variants (Red, Black, etc.)
 * - Material variants (Stainless Steel, etc.)
 * - Size variants (63mm, 160mm, etc.)
 * - Any other variant types
 * 
 * Accessories are messy and inconsistent in Cova, so we normalize all legacy data
 * into this uniform structure using 4 parsing cases (A, B, C, D).
 * 
 * IMPORTANT: Trust Cova brandName field FIRST when available
 */
function parseAccessoryProduct(
  name: string,
  classificationName: string | null,
  brandName: string | null
): ParsedProduct {
  const segments = name.split('|').map(s => s.trim()).filter(s => s.length > 0);
  
  // Known brand list for inference (common accessory brands)
  const knownBrands = [
    'yocan', 'dank', 'dank logo', 'crossing tech', 'fuzion', 'pulsar', 'armor quartz',
    'herbalaire', 'glass', 'test', 'uni', 'gear premium'
  ];
  
  // Trust Cova brandName field FIRST
  let brand: string | null = brandName || null;
  let productName = name;
  let variant1: string | null = null;
  let variant2: string | null = null;
  
  // Case A: 3-4 pipe segments
  // Rule: Brand = segment 1, Product Name = segment 2, Variant 1 = segment 3, Variant 2 = segment 4 if present
  // Examples: "Yocan | Uni Pro | 510 Battery | Red" or "Dank Logo | Aluminum Grinder | 63mm | Black"
  // Special case for clothing/merch:
  //   - 3 segments: "Dank Socks | Dank Logo Sports Black | Women's Size US 6-9"
  //     → Product Name: "Sock - Dank Logo Sports Black" (singularize product type + combine with segment 2)
  //   - 4 segments: "Dank Pants | Call My Dealer | Dark Heather | Small"
  //     → Product Name: "Pant - Call My Dealer - Dark Heather" (Product Type + Spec 1 + Spec 2)
  //     → Variant: "Small" (Spec 3)
  if (segments.length >= 3) {
    const segment1 = segments[0] || '';
    const segment2 = segments[1] || '';
    const segment3 = segments[2] || '';
    const segment4 = segments[3] || null;
    
    // Check if this is a clothing/merch product with plural product type in segment 1
    // Pattern: "Brand ProductType" (e.g., "Dank Socks", "Dank T-Shirt", "Dank Hat")
    // Common clothing product types (plural forms)
    const clothingProductTypes = [
      { plural: 'socks', singular: 'sock' },
      { plural: 'shirts', singular: 'shirt' },
      { plural: 'hats', singular: 'hat' },
      { plural: 'hoodies', singular: 'hoodie' },
      { plural: 'sweatshirts', singular: 'sweatshirt' },
      { plural: 'pants', singular: 'pant' },
      { plural: 'shorts', singular: 'short' },
      { plural: 'jackets', singular: 'jacket' },
      { plural: 't-shirts', singular: 't-shirt' },
      { plural: 'tanks', singular: 'tank' },
      { plural: 'tee', singular: 'tee' },
      { plural: 'beanies', singular: 'beanie' },
      { plural: 'caps', singular: 'cap' },
    ];
    
    const segment1Lower = segment1.toLowerCase();
    const segment4Lower = (segment4 || '').toLowerCase().trim();
    let isClothingProduct = false;
    let singularProductType: string | null = null;
    let productTypeFromSegment4 = false;
    
    // First, check segment 4 for product type (e.g., "Oversized Hoodie")
    // This takes priority as it's more specific
    if (segments.length === 4 && segment4) {
      for (const { plural, singular } of clothingProductTypes) {
        // Check if segment 4 contains the product type (e.g., "Oversized Hoodie")
        if (segment4Lower.includes(` ${plural}`) || segment4Lower.includes(` ${singular}`) || 
            segment4Lower.endsWith(plural) || segment4Lower.endsWith(singular) ||
            segment4Lower.startsWith(plural) || segment4Lower.startsWith(singular)) {
          isClothingProduct = true;
          singularProductType = singular;
          productTypeFromSegment4 = true;
          break;
        }
      }
    }
    
    // If not found in segment 4, check segment 1 for product type (anywhere in the string)
    if (!isClothingProduct) {
      for (const { plural, singular } of clothingProductTypes) {
        // Check if segment 1 ends with the plural form (e.g., "Dank Socks", "Dank Hoodies")
        if (segment1Lower.endsWith(` ${plural}`) || segment1Lower === plural) {
          isClothingProduct = true;
          singularProductType = singular;
          break;
        }
        // Also check for singular form at the end (e.g., "Dank Pom Pom Beanie")
        if (segment1Lower.endsWith(` ${singular}`) || segment1Lower === singular) {
          isClothingProduct = true;
          singularProductType = singular;
          break;
        }
        // Check if product type appears anywhere in segment 1 (e.g., "Dank Snapback Cap w/ Joint Holder")
        if (segment1Lower.includes(` ${plural} `) || segment1Lower.includes(` ${singular} `) ||
            segment1Lower.includes(` ${plural}`) || segment1Lower.includes(` ${singular}`)) {
          isClothingProduct = true;
          singularProductType = singular;
          break;
        }
      }
    }
    
    if (isClothingProduct && singularProductType) {
      // Clothing product: Brand is segment 2, segments 3 and 4 are variants/options
      // For clothing: Segment 1 = Product Type, Segment 2 = Brand, Segment 3 = Variant 1, Segment 4 = Variant 2
      // Example: "Dank Pants | Call My Dealer | Dark Heather | Small"
      //   → Brand: "Call My Dealer" (segment 2)
      //   → Variant 1: "Dark Heather" (segment 3)
      //   → Variant 2: "Small" (segment 4)
      brand = brand || segment2 || null;
      
      if (segments.length === 4) {
        // 4 segments: Product Type | Specification 1 | Specification 2 | Specification 3
        // Need to determine if segment 3 is a color variant or part of the product name
        // Common color keywords (including multi-word colors)
        const colorKeywords = ['pink', 'white', 'yellow', 'red', 'blue', 'green', 'black', 'gray', 'grey', 'purple', 'orange', 'brown', 'beige', 'navy', 'maroon', 'teal', 'coral', 'mint', 'lavender', 'tan', 'khaki', 'olive', 'burgundy', 'charcoal', 'cream', 'ivory', 'peach', 'salmon', 'turquoise', 'violet', 'amber', 'aqua', 'lime', 'magenta', 'cyan', 'indigo', 'fuchsia', 'gold', 'silver', 'bronze', 'copper', 'forest green', 'dark green', 'light green', 'emerald', 'sage', 'mint green', 'hunter green', 'army green', 'dark blue', 'light blue', 'sky blue', 'royal blue', 'navy blue', 'powder blue', 'dark gray', 'light gray', 'charcoal gray', 'dark grey', 'light grey', 'charcoal grey', 'dark brown', 'light brown', 'chocolate', 'coffee', 'caramel', 'dark red', 'light red', 'crimson', 'scarlet', 'burgundy red', 'dark pink', 'light pink', 'hot pink', 'rose', 'blush', 'dark purple', 'light purple', 'lavender purple', 'dark orange', 'light orange', 'burnt orange', 'dark yellow', 'light yellow', 'dark heather', 'heather', 'light heather', 'mustard', 'dark black', 'jet black', 'midnight black'];
        const segment3Lower = (segment3 || '').toLowerCase().trim();
        // Check if segment 3 is a color variant (exact match or contains color keyword)
        const isColorVariant = colorKeywords.some(color => 
          segment3Lower === color || segment3Lower.includes(color)
        );
        
        // Common size patterns
        const sizePatterns = ['one size', 'onesize', 'os', 's', 'm', 'l', 'xl', 'xxl', 'xxxl', 'xs', '2xl', '3xl', '4xl', 'small', 'medium', 'large', 'extra large', 'extra small'];
        const isSizeVariant = sizePatterns.some(pattern => segment4Lower.includes(pattern));
        
        // Check if segment 4 contains a product type (e.g., "Oversized Hoodie")
        const isProductTypeInSegment4 = productTypeFromSegment4;
        
        // We know singularProductType is not null because we're inside the if (isClothingProduct && singularProductType) block
        const capitalizedProductType = singularProductType.charAt(0).toUpperCase() + singularProductType.slice(1);
        
        if (isColorVariant && (isSizeVariant || isProductTypeInSegment4)) {
          // Pattern: Product Name | Specification 1 | Color | Size/Type
          // Example 1: "Dank Pom Pom Beanie | Dank Logo | Pink | One Size"
          //   → Product Name: "Beanie - Dank Logo" (group all colors together)
          //   → Variant 1: "Pink", "White", "Yellow" (color)
          //   → Variant 2: "One Size" (size)
          // Example 2: "The Dankaroo | Dank Logo | Blue | Oversized Hoodie"
          //   → Product Name: "The Dankaroo - Dank Logo" (keep segment 1 as-is, combine with segment 2)
          //   → Variant 1: "Blue" (color)
          //   → Variant 2: "Oversized Hoodie" (size/weight option)
          if (isProductTypeInSegment4) {
            // When segment 4 contains product type, use segment 1 as-is (don't extract/singularize)
            productName = `${segment1} - ${segment2}`;
          } else {
            // Standard case: use extracted product type
            productName = `${capitalizedProductType} - ${segment2}`;
          }
          variant1 = segment3 || null;  // Color
          variant2 = segment4 || null;  // Size or Type modifier
        } else if (isColorVariant) {
          // Pattern: Product Type | Specification 1 | Color | Other
          // Example: "Dank Snapback Cap w/ Joint Holder | Dank Logo | Pink | One Size"
          //   → Product Name: "Cap - Dank Logo" (group all colors together)
          //   → Variant 1: "Pink", "Black", "Blue" (color)
          //   → Variant 2: "One Size" (size)
          productName = `${capitalizedProductType} - ${segment2}`;
          variant1 = segment3 || null;  // Color
          variant2 = segment4 || null;  // Other
        } else {
          // Pattern: Product Type | Specification 1 | Specification 2 | Specification 3 (variant)
          // Example: "Dank Pants | Call My Dealer | Dark Heather | Small"
          //   → Product Name: "Pant - Call My Dealer - Dark Heather"
          //   → Variant: "Small"
          productName = `${capitalizedProductType} - ${segment2} - ${segment3}`;
          variant1 = segment4 || null;
          variant2 = null;
        }
      } else {
        // 3 segments: Product Type | Specification 1 | Specification 2 (variant)
        // Example: "Dank Socks | Dank Logo Sports Black | Women's Size US 6-9"
        //   → Product Name: "Sock - Dank Logo Sports Black"
        //   → Variant: "Women's Size US 6-9"
        // We know singularProductType is not null because we're inside the if (isClothingProduct && singularProductType) block
        const capitalizedProductType = singularProductType.charAt(0).toUpperCase() + singularProductType.slice(1);
        productName = `${capitalizedProductType} - ${segment2}`;
        variant1 = segment3 || null;
        variant2 = null;
      }
    } else {
      // Standard 3-segment parsing (non-clothing)
      // Use Cova brand if provided, otherwise use segment 1
      brand = brand || segment1 || null;
      productName = segment2 || '';
      variant1 = segment3 || null;  // e.g., "510 Battery", "63mm", "Glass Recycler Upgrade"
      variant2 = segment4 || null;  // e.g., "Red", "Black" (only if 4 segments)
    }
  }
  // Case B: 2 pipe segments
  // Rule: Need to determine if it's "Brand | Product Name" or "Product Name | Variant"
  // Heuristic: If segment 2 is a single short word (likely color/size variant), treat as Product|Variant
  // Otherwise, treat as Brand|Product
  // Examples: 
  //   "Dank | Lanyard" → Brand: Dank, Product: Lanyard
  //   "Air Dank Mini Basketball | Yellow" → Product: Air Dank Mini Basketball, Variant1: Yellow
  else if (segments.length === 2) {
    const segment1 = segments[0] || '';
    const segment2 = segments[1] || '';
    
    // Check if segment 2 looks like a variant (single word, common variant terms)
    // Only treat as variant if it's explicitly a variant keyword or starts with number
    // Product names like "Lanyard" should NOT be treated as variants
    const variantKeywords = ['red', 'blue', 'green', 'yellow', 'black', 'white', 'silver', 'gold', 
                             'pink', 'clear', 'small', 'medium', 'large', 'xl', 'xxl', '5pcs', '10pcs', 'assorted'];
    const segment2Lower = segment2.toLowerCase().trim();
    const isLikelyVariant = segment2.split(/\s+/).length === 1 && 
                           (variantKeywords.includes(segment2Lower) || 
                            /^\d+/.test(segment2Lower)); // Starts with number (e.g., "5pcs", "10pcs")
    
    if (isLikelyVariant) {
      // Treat as "Brand + Product Name | Variant" or "Product Name | Variant"
      // If segment 1 has multiple words, try to extract brand from first word
      variant1 = segment2;
      
      const segment1Words = segment1.split(/\s+/).filter(w => w.length > 0);
      
      // Check if segment 1 contains both brand and product name
      // Example: "Dank Water Bottle Pasties" → Brand: "Dank", Product: "Water Bottle Pasties"
      if (segment1Words.length > 1) {
        // Trust Cova brandName FIRST
        if (!brand) {
          const firstWord = segment1Words[0].toLowerCase();
          // Check if first word matches a known brand
          const matchedBrand = knownBrands.find(b => 
            firstWord === b.toLowerCase() || 
            firstWord.startsWith(b.toLowerCase()) ||
            b.toLowerCase().includes(firstWord)
          );
          
          if (matchedBrand) {
            // Found known brand - extract it
            brand = matchedBrand;
            // Product name is the rest of segment 1
            productName = segment1Words.slice(1).join(' ').trim();
          } else {
            // Check if first word could be a brand (short, capitalized)
            const potentialBrand = segment1Words[0];
            if (potentialBrand.length <= 15 && /^[A-Z]/.test(potentialBrand)) {
              brand = potentialBrand;
              productName = segment1Words.slice(1).join(' ').trim();
            } else {
              // Can't confidently identify brand, treat entire segment 1 as product name
              productName = segment1;
              brand = null;
            }
          }
        } else {
          // Cova brand provided, product name is the rest of segment 1
          productName = segment1Words.slice(1).join(' ').trim() || segment1;
        }
      } else {
        // Single word in segment 1 - treat as product name
        productName = segment1;
        
        // Brand: Trust Cova brandName FIRST
        // If not provided, try to infer from product name
        if (!brand) {
          const productWords = productName.split(/\s+/).filter(w => w.length > 0);
          if (productWords.length > 0) {
            const firstWord = productWords[0].toLowerCase();
            // Check if first word matches a known brand
            const matchedBrand = knownBrands.find(b => 
              firstWord === b.toLowerCase() || 
              firstWord.startsWith(b.toLowerCase()) ||
              b.toLowerCase().includes(firstWord)
            );
            
            if (matchedBrand) {
              brand = matchedBrand;
              // Keep full product name as is (e.g., "Air Dank Mini Basketball" stays intact)
            } else {
              // Check if first word or first two words could be brand
              // For "Air Dank Mini Basketball", "Dank" is the brand
              if (productWords.length >= 2) {
                // Check if second word is a known brand (e.g., "Air Dank" → "Dank")
                const secondWord = productWords[1].toLowerCase();
                const matchedBrand2 = knownBrands.find(b => 
                  secondWord === b.toLowerCase() || 
                  secondWord.startsWith(b.toLowerCase()) ||
                  b.toLowerCase().includes(secondWord)
                );
                
                if (matchedBrand2) {
                  brand = matchedBrand2;
                  // Keep full product name as is (e.g., "Air Dank Mini Basketball")
                } else {
                  // Can't confidently identify brand → Brand = Unknown
                  brand = null;
                }
              } else {
                brand = null;
              }
            }
          } else {
            brand = null;
          }
        }
      }
    } else {
      // Treat as "Brand | Product Name"
      brand = brand || segment1 || null;
      productName = segment2;
      // Variants remain null
    }
  }
  // Case C: Hyphens, no pipes
  // Rule: Split on hyphen, Left side = Product Name, Right side = Variant 1
  // Brand inferred from first word or known brand list
  // If brand cannot be confidently identified, set Brand to Unknown
  // IMPORTANT: Keep full product name intact (don't remove brand from product name)
  // Examples: "Glass blunt- Black" → Product: "Glass blunt", Variant: "Black", Brand: "Glass" or Unknown
  //           "Dank Ashtray- Assorted" → Product: "Dank Ashtray", Variant: "Assorted", Brand: "Dank"
  //           "Armor Quartz Dual Coil- 5pcs" → Product: "Armor Quartz Dual Coil", Variant: "5pcs", Brand: "Armor Quartz" or Unknown
  else if (name.includes('-') && !name.includes('|')) {
    // Split on first hyphen only (left = Product Name, right = Variant 1)
    const hyphenIndex = name.indexOf('-');
    if (hyphenIndex > 0) {
      productName = name.substring(0, hyphenIndex).trim();
      variant1 = name.substring(hyphenIndex + 1).trim();
      
      // Brand: Trust Cova brandName FIRST
      // If not provided, try to infer from product name (but keep full product name intact)
      if (!brand) {
        const productWords = productName.split(/\s+/).filter(w => w.length > 0);
        if (productWords.length > 0) {
          const firstWord = productWords[0].toLowerCase();
          // Check if first word matches a known brand
          const matchedBrand = knownBrands.find(b => 
            firstWord === b.toLowerCase() || 
            firstWord.startsWith(b.toLowerCase()) ||
            b.toLowerCase().includes(firstWord)
          );
          
          if (matchedBrand) {
            // Found known brand - use it, but keep full product name
            brand = matchedBrand;
            // Keep full product name as is (e.g., "Glass blunt" stays "Glass blunt")
          } else {
            // Check if first word or first two words could be brand
            // For "Glass blunt- Black", first word is "Glass" which could be brand
            // For "Dank Ashtray- Assorted", first word is "Dank" which is a known brand
            // For "Armor Quartz Dual Coil- 5pcs", first word is "Armor" which could be part of "Armor Quartz"
            if (productWords.length > 1) {
              // Check if first word is a known brand (e.g., "Dank" in "Dank Ashtray")
              const firstWordLower = firstWord;
              const matchedBrandFirst = knownBrands.find(b => 
                firstWordLower === b.toLowerCase() || 
                firstWordLower.startsWith(b.toLowerCase()) ||
                b.toLowerCase().includes(firstWordLower)
              );
              
              if (matchedBrandFirst) {
                brand = matchedBrandFirst;
                // Keep full product name as is
              } else {
                // Check if first two words together could be a brand (e.g., "Armor Quartz")
                if (productWords.length >= 2) {
                  const firstTwoWords = `${productWords[0]} ${productWords[1]}`.toLowerCase();
                  const matchedBrandTwo = knownBrands.find(b => 
                    firstTwoWords === b.toLowerCase() || 
                    firstTwoWords.startsWith(b.toLowerCase()) ||
                    b.toLowerCase().includes(firstTwoWords)
                  );
                  
                  if (matchedBrandTwo) {
                    brand = matchedBrandTwo;
                    // Keep full product name as is
                  } else {
                    // Try first word as brand if it looks like one (short, capitalized)
                    const potentialBrand = productWords[0];
                    if (potentialBrand.length <= 15 && /^[A-Z]/.test(potentialBrand)) {
                      brand = potentialBrand;
                      // Keep full product name as is
                    } else {
                      // Can't confidently identify brand → Brand = Unknown
                      brand = null;
                    }
                  }
                } else {
                  // Can't confidently identify brand → Brand = Unknown
                  brand = null;
                }
              }
            } else {
              // Single word product name, can't infer brand → Brand = Unknown
              brand = null;
            }
          }
        } else {
          brand = null;
        }
      }
    } else {
      productName = name;
      brand = null;
    }
  }
  // Case D: Single segment, no separators
  // Rule: Brand = first word, Product Name = remaining words
  // If first word is not a brand and no inference possible, Brand = Unknown
  // Examples: "Uni Mod Silver", "Test Accessory 1", "HerbalAire H3 Desktop Vaporizer"
  else {
    const words = name.split(/\s+/).filter(w => w.length > 0);
    if (words.length >= 2) {
      // Brand: Trust Cova brandName FIRST
      if (!brand) {
        const firstWord = words[0];
        // Always use first word as brand (per rules)
        brand = firstWord;
        productName = words.slice(1).join(' ');
      } else {
        // Cova brand provided, product name is everything else
        productName = words.join(' ');
      }
    } else {
      // Single word - treat as product name, no brand (Brand = Unknown)
      productName = name;
      brand = null;
    }
  }
  
  // Clean up product name (remove pipes, normalize spaces)
  productName = productName.replace(/\|/g, '').replace(/\s+/g, ' ').trim();
  
  // Ensure we have a valid product name
  if (!productName || productName.length === 0) {
    productName = name; // Fallback to original name
  }
  
  return {
    brand,
    productName,
    classification: classificationName,
    weight: null,
    variant1,
    variant2,
    isCannabis: false,
    isFlower: false,
  };
}

/**
 * Parse product name according to ruleset
 */
/**
 * Parse product name into structured data
 * 
 * OVERRIDE SYSTEM: If override values are provided, they win over parsed values.
 * This allows manual correction of bad Cova data and custom SEO.
 * 
 * @param name - Product name from Cova
 * @param classificationName - Classification from Cova (trusted FIRST)
 * @param brandName - Brand from Cova (trusted FIRST)
 * @param sizeOrWeight - Size/Weight from Cova (trusted FIRST)
 * @param overrides - Optional override values that win over parsed values
 * @returns ParsedProduct with parsed or overridden values
 */
export function parseProduct(
  name: string,
  classificationName: string | null,
  brandName: string | null,
  sizeOrWeight: string | null,
  overrides?: {
    brand?: string | null;
    productName?: string | null;
    classification?: string | null;
    variant1?: string | null;
    variant2?: string | null;
  }
): ParsedProduct {
  const isCannabis = isCannabisProduct(classificationName);
  
  let parsed: ParsedProduct;
  if (isCannabis) {
    parsed = parseCannabisProduct(name, classificationName, brandName, sizeOrWeight);
  } else {
    parsed = parseAccessoryProduct(name, classificationName, brandName);
  }
  
  // Apply overrides if provided (override wins)
  if (overrides) {
    if (overrides.brand !== undefined) parsed.brand = overrides.brand;
    if (overrides.productName !== undefined) parsed.productName = overrides.productName || parsed.productName;
    if (overrides.classification !== undefined) parsed.classification = overrides.classification;
    if (overrides.variant1 !== undefined) parsed.variant1 = overrides.variant1;
    if (overrides.variant2 !== undefined) parsed.variant2 = overrides.variant2;
    
    // Recalculate isFlower if classification was overridden
    if (overrides.classification !== undefined) {
      parsed.isFlower = isFlowerProduct(overrides.classification);
    }
  }
  
  return parsed;
}

/**
 * Remove noise words from text for slug generation
 * 
 * Noise words to remove: "the", "with", "by", "from", "a", "an", "and", "or", "of", "in", "on", "at", "to", "for"
 */
function removeFillerWords(text: string): string {
  const noiseWords = ['the', 'with', 'by', 'from', 'a', 'an', 'and', 'or', 'of', 'in', 'on', 'at', 'to', 'for'];
  const words = text.split(/\s+/);
  return words
    .filter(word => !noiseWords.includes(word.toLowerCase()))
    .join(' ');
}

/**
 * Generate slug from text following cannabis URL rules:
 * - lowercase
 * - hyphens
 * - remove special chars
 * - normalize accents
 */
function generateSlug(text: string): string {
  if (!text) return '';
  
  return text
    .toLowerCase()                    // lowercase
    .trim()
    .normalize('NFD')                 // normalize accents
    .replace(/[\u0300-\u036f]/g, '')  // remove accent marks
    .replace(/[^a-z0-9]+/g, '-')      // remove special chars, use hyphens
    .replace(/^-+|-+$/g, '')          // remove leading/trailing hyphens
    .replace(/-+/g, '-')               // remove duplicate hyphens
    .substring(0, 100)                // limit length
    .replace(/-+$/g, '');             // remove trailing hyphens after truncation
}

/**
 * Generate product URL slug according to cannabis URL rules
 * 
 * CANNABIS URL RULES:
 * - Non-flower: /products/{product-name}-{brand}
 * - Flower: /products/{product-name}-{classification}-{brand}
 *   (Classification required because flower names don't contain "flower")
 * 
 * SLUG RULES (applied to all parts):
 * - lowercase
 * - hyphens
 * - remove special chars
 * - remove noise words ("the", "with", "by", etc.)
 * - normalize accents
 * - NEVER include weight
 */
export function generateProductSlug(parsed: ParsedProduct): string {
  const { productName, brand, classification, variant1, variant2, isCannabis, isFlower } = parsed;
  
  if (!productName) return '';
  
  // Remove noise words from product name
  const cleanedProductName = removeFillerWords(productName);
  const productSlug = generateSlug(cleanedProductName);
  
  if (isCannabis) {
    // Cannabis URL Rules
    if (isFlower) {
      // Flower: /products/{product-name}-{classification}-{brand}
      // Classification is required because flower names don't contain "flower"
      const classificationSlug = classification ? generateSlug(removeFillerWords(classification)) : '';
      const brandSlug = brand ? generateSlug(removeFillerWords(brand)) : '';
      
      const parts = [productSlug];
      if (classificationSlug) parts.push(classificationSlug);
      if (brandSlug) parts.push(brandSlug);
      
      return parts.join('-');
    } else {
      // Non-flower: /products/{product-name}-{brand}
      const brandSlug = brand ? generateSlug(removeFillerWords(brand)) : '';
      
      if (brandSlug) {
        return `${productSlug}-${brandSlug}`;
      }
      return productSlug;
    }
  } else {
    // Accessory URL Rules
    // URL depends on what's available:
    // - If Product + Brand: /products/{product-name}-{brand}
    // - With variant1: /products/{product-name}-{variant1}-{brand}
    // - With variant1 + variant2: /products/{product-name}-{variant1}-{variant2}-{brand}
    // Slug rules match cannabis (lowercase, hyphens, remove special chars, remove noise words, normalize accents)
    const brandSlug = brand ? generateSlug(removeFillerWords(brand)) : '';
    const variant1Slug = variant1 ? generateSlug(removeFillerWords(variant1)) : '';
    const variant2Slug = variant2 ? generateSlug(removeFillerWords(variant2)) : '';
    
    const parts = [productSlug];
    if (variant1Slug) parts.push(variant1Slug);
    if (variant2Slug) parts.push(variant2Slug);
    if (brandSlug) parts.push(brandSlug);
    
    return parts.join('-');
  }
}

/**
 * Generate SEO title according to cannabis SEO rules
 * 
 * CANNABIS SEO TITLE RULES:
 * - Non-flower: {product-name} | {brand} | Dank
 * - Flower: {product-name} | {brand} | {classification} | Dank
 */
export function generateSEOTitle(parsed: ParsedProduct, overrideTitle?: string | null): string {
  if (overrideTitle) return overrideTitle;
  
  const { productName, brand, classification, variant1, isCannabis, isFlower } = parsed;
  
  if (!productName) return 'Dank';
  
  if (isCannabis) {
    // Cannabis SEO Title Rules - Simple and clean
    if (isFlower) {
      // Flower: {product-name} | {brand} | {classification} | Dank
      const parts = [productName];
      if (brand) parts.push(brand);
      if (classification) parts.push(classification);
      parts.push('Dank');
      return parts.join(' | ');
    } else {
      // Non-flower: {product-name} | {brand} | Dank
      const parts = [productName];
      if (brand) parts.push(brand);
      parts.push('Dank');
      return parts.join(' | ');
    }
  } else {
    // Accessory SEO Title Rules
    // Base title: {product-name} | {brand} | Dank
    // If variant exists: {product-name} {variant1} | {brand} | Dank
    // If brand missing → fallback: {product-name} | Dank
    let displayName = productName;
    if (variant1) {
      displayName = `${productName} ${variant1}`;
    }
    
    const parts = [displayName];
    if (brand) parts.push(brand);
    parts.push('Dank');
    return parts.join(' | ');
  }
}

/**
 * Generate SEO description according to cannabis SEO rules
 * 
 * CANNABIS SEO DESCRIPTION RULES:
 * - Non-flower: {product-name} by {brand}. Shop at Dank.
 * - Flower: {product-name} {classification} by {brand}. Shop at Dank.
 */
export function generateSEODescription(parsed: ParsedProduct, overrideDescription?: string | null): string {
  if (overrideDescription) return overrideDescription;
  
  const { productName, brand, classification, variant1, isCannabis, isFlower } = parsed;
  
  if (!productName) return 'Shop at Dank.';
  
  if (isCannabis) {
    // Cannabis SEO Description Rules - Simple and clean
    if (isFlower) {
      // Flower: {product-name} {classification} by {brand}. Shop at Dank.
      if (brand) {
        return `${productName} ${classification || ''} by ${brand}. Shop at Dank.`.trim();
      }
      return `${productName} ${classification || ''}. Shop at Dank.`.trim();
    } else {
      // Non-flower: {product-name} by {brand}. Shop at Dank.
      if (brand) {
        return `${productName} by ${brand}. Shop at Dank.`;
      }
      return `${productName}. Shop at Dank.`;
    }
  } else {
    // Accessory SEO Description Rules
    // Base description: {product-name} by {brand}. Shop at Dank.
    // If variant exists: {product-name} {variant1} by {brand}. Shop at Dank.
    // If brand missing → fallback: {product-name}. Shop at Dank.
    // (If variant exists and brand missing: {product-name} {variant1}. Shop at Dank.)
    let displayName = productName;
    if (variant1) {
      displayName = `${productName} ${variant1}`;
    }
    
    if (brand) {
      return `${displayName} by ${brand}. Shop at Dank.`;
    }
    // Brand missing → fallback
    return `${displayName}. Shop at Dank.`;
  }
}

